import {
  AllowancesRulesFragment,
  TerminationRulesFragment,
  CalendarDurationFragment,
  TimeOffRulesFragment,
} from "@oysterhr/advisor"
import { gql, useLazyQuery } from "@apollo/client"
import { CountryIntelligenceCollection } from "@oysterhr/schema"
import { InjectedOptions } from "@oysterhr/core"

export interface Variables {
  countryCodes: string[]
  expandSubdivisions: boolean
  probationPeriod?: boolean
  vacationDays?: boolean
  sickDays?: boolean
  parentalLeave?: boolean
  terminationNotice?: boolean
  benefits?: boolean
  allowance?: boolean
  page?: number
  perPage?: number
}

export interface Response {
  countryIntelligences: CountryIntelligenceCollection
}

const GET_COUNTRY_INTELLIGENCE = gql`
  query CountryIntelligence(
    $countryCodes: [String!]!
    $expandSubdivisions: Boolean!
    $probationPeriod: Boolean!
    $vacationDays: Boolean!
    $sickDays: Boolean!
    $parentalLeave: Boolean!
    $terminationNotice: Boolean!
    $benefits: Boolean!
    $allowance: Boolean!
    $page: Int
    $perPage: Int
  ) {
    countryIntelligences(
      countryCodes: $countryCodes
      expandSubdivisions: $expandSubdivisions
      perPage: $perPage
      page: $page
    ) {
      collection {
        name
        isoAlpha2Code
        subdivisionCodes
        allowancesRules @include(if: $allowance) {
          ...AllowancesRulesFragment
        }
        countryBenefits(perPage: 100) @include(if: $benefits) {
          collection {
            benefit
            comment
            countryCode
            tier
          }
        }
        terminationRules @include(if: $terminationNotice) {
          ...TerminationRulesFragment
          oysterScore
        }
        paternityLeaveRules: timeOffRules(type: PATERNITY) @include(if: $parentalLeave) {
          ...TimeOffRulesFragment
        }
        maternityLeaveRules: timeOffRules(type: MATERNITY) @include(if: $parentalLeave) {
          ...TimeOffRulesFragment
        }
        sickLeaveRules: timeOffRules(type: SICK_LEAVE) @include(if: $sickDays) {
          ...TimeOffRulesFragment
        }
        vacationRules: timeOffRules(type: VACATIONS) @include(if: $vacationDays) {
          ...TimeOffRulesFragment
        }
        probationRules @include(if: $probationPeriod) {
          duration {
            ...CalendarDurationFragment
          }
          oysterScore
        }
      }
      metadata {
        currentPage
        totalCount
        totalPages
        limitValue
      }
    }
  }
  ${AllowancesRulesFragment}
  ${TerminationRulesFragment}
  ${CalendarDurationFragment}
  ${TimeOffRulesFragment}
`

export const useCountryIntelligence = (options?: InjectedOptions<Response, Variables>) =>
  useLazyQuery<Response, Variables>(GET_COUNTRY_INTELLIGENCE, options)
